export type AppTheme = "dark" | "light";

export const DEFAULT_THEME: AppTheme = "light";
export const THEME_STORAGE_KEY = "tu-conferences-theme";

export interface ThemeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ThemeDocumentTarget {
  dataset: { theme?: string };
}

function availableBrowserStorage(): ThemeStorage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function availableDocumentTarget(): ThemeDocumentTarget | undefined {
  return typeof document === "undefined" ? undefined : document.documentElement;
}

export function isSupportedTheme(value: unknown): value is AppTheme {
  return value === "dark" || value === "light";
}

export function readStoredTheme(
  storage: ThemeStorage | undefined = availableBrowserStorage(),
): AppTheme | null {
  if (!storage) return null;
  try {
    const storedTheme = storage.getItem(THEME_STORAGE_KEY);
    return isSupportedTheme(storedTheme) ? storedTheme : null;
  } catch {
    return null;
  }
}

export function resolveInitialTheme(
  storage: ThemeStorage | undefined = availableBrowserStorage(),
  target: ThemeDocumentTarget | undefined = availableDocumentTarget(),
): AppTheme {
  return (
    readStoredTheme(storage) ??
    (isSupportedTheme(target?.dataset.theme)
      ? target.dataset.theme
      : DEFAULT_THEME)
  );
}

export function persistTheme(
  theme: AppTheme,
  storage: ThemeStorage | undefined = availableBrowserStorage(),
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
    return true;
  } catch {
    return false;
  }
}

export function applyThemeToDocument(
  theme: AppTheme,
  target: ThemeDocumentTarget | undefined = availableDocumentTarget(),
): void {
  if (!target) return;
  target.dataset.theme = theme;
  if (typeof document !== "undefined" && target === document.documentElement) {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#101C19" : "#F7F8FA");
  }
}
